import React, { useState } from 'react';
import { useWinners, useContests } from '../../hooks/useAdmin';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Trophy, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Search,
  Filter,
  Medal,
  DollarSign,
  User,
  Calendar,
  FileText,
  AlertTriangle,
  Plus,
  Eye
} from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';

const WinnerManager = () => {
  const { winners, loading: winnersLoading, error: winnersError, updateWinnerStatus } = useWinners();
  const { contests } = useContests();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [contestFilter, setContestFilter] = useState('all');
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Filter winners
  const filteredWinners = winners.filter(winner => {
    const matchesSearch = winner.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         winner.contest_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || winner.status === statusFilter;
    const matchesContest = contestFilter === 'all' || winner.contest_id === contestFilter;
    return matchesSearch && matchesStatus && matchesContest;
  });

  const getStatusBadge = (status) => {
    const variants = {
      pending: { className: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      verified: { className: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Verified' },
      disqualified: { className: 'bg-red-100 text-red-800', icon: XCircle, label: 'Disqualified' },
      prize_distributed: { className: 'bg-blue-100 text-blue-800', icon: Trophy, label: 'Prize Distributed' }
    };
    const variant = variants[status] || variants.pending;
    const Icon = variant.icon;
    return (
      <Badge className={variant.className}>
        <Icon className="w-3 h-3 mr-1" />
        {variant.label}
      </Badge>
    );
  };

  const getPositionBadge = (position) => {
    const colors = {
      1: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      2: 'bg-gray-100 text-gray-800 border-gray-300',
      3: 'bg-orange-100 text-orange-800 border-orange-300'
    };
    const color = colors[position] || 'bg-blue-100 text-blue-800 border-blue-300';
    
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${color}`}>
        <Medal className="w-3 h-3 mr-1" />
        {position === 1 ? '1st' : position === 2 ? '2nd' : position === 3 ? '3rd' : `${position}th`}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const handleStatusUpdate = async (winnerId, newStatus) => {
    try {
      await updateWinnerStatus(winnerId, newStatus);
    } catch (error) {
      console.error('Failed to update winner status:', error);
    }
  };

  const viewDetails = (winner) => {
    setSelectedWinner(winner);
    setShowDetails(true);
  };

  const calculateStats = () => {
    const total = winners.length;
    const pending = winners.filter(w => w.status === 'pending').length;
    const verified = winners.filter(w => w.status === 'verified').length;
    const distributed = winners.filter(w => w.status === 'prize_distributed').length;
    const totalPrizes = winners.reduce((sum, winner) => sum + winner.prize_amount, 0);
    const distributedPrizes = winners
      .filter(w => w.status === 'prize_distributed')
      .reduce((sum, winner) => sum + winner.prize_amount, 0);

    return {
      total,
      pending,
      verified,
      distributed,
      totalPrizes,
      distributedPrizes
    };
  };

  const stats = calculateStats();

  if (winnersLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Winner Management</h1>
          <p className="text-gray-600">Verify winners and manage prize distribution</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Winner
        </Button>
      </div>

      {winnersError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{winnersError}</AlertDescription>
        </Alert>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Winners</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-gray-500">All contests</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Pending Verification</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <p className="text-xs text-gray-500">Awaiting verification</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Verified</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.verified}</div>
            <p className="text-xs text-gray-500">Ready for distribution</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Prizes Distributed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.distributedPrizes)}</div>
            <p className="text-xs text-gray-500">Out of {formatCurrency(stats.totalPrizes)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by user ID or contest ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="disqualified">Disqualified</option>
              <option value="prize_distributed">Prize Distributed</option>
            </select>
            
            <select
              value={contestFilter}
              onChange={(e) => setContestFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Contests</option>
              {contests.map(contest => (
                <option key={contest.id} value={contest.id}>
                  {contest.title}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Winner List */}
      <div className="space-y-4">
        {filteredWinners.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No winners found</h3>
              <p className="text-gray-600">
                {searchTerm || statusFilter !== 'all' || contestFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Winners will appear here once contests are completed and results are processed.'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredWinners.map((winner) => {
            const contest = contests.find(c => c.id === winner.contest_id);
            
            return (
              <Card key={winner.id} className="hover:shadow-lg transition-shadow duration-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-yellow-100 rounded-full">
                        <Trophy className="h-4 w-4 text-yellow-600" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-medium text-gray-900">Winner #{winner.id.slice(-8)}</h3>
                          {getPositionBadge(winner.position)}
                        </div>
                        <p className="text-sm text-gray-600">
                          Contest: {contest?.title || winner.contest_id}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(winner.status)}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <div className="text-sm">
                        <p className="text-gray-600">User ID</p>
                        <p className="font-medium">{winner.user_id}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                      <div className="text-sm">
                        <p className="text-gray-600">Prize Amount</p>
                        <p className="font-medium text-green-600">{formatCurrency(winner.prize_amount)}</p>
                      </div>
                    </div>
                    
                    {winner.score && (
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <div className="text-sm">
                          <p className="text-gray-600">Score</p>
                          <p className="font-medium">{winner.score}</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <div className="text-sm">
                        <p className="text-gray-600">Created</p>
                        <p className="font-medium">{formatDate(winner.created_at)}</p>
                      </div>
                    </div>
                  </div>
                  
                  {(winner.verified_at || winner.prize_distributed_at) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                      {winner.verified_at && (
                        <div className="text-sm">
                          <p className="text-gray-600">Verified At</p>
                          <p className="font-medium text-green-600">{formatDate(winner.verified_at)}</p>
                        </div>
                      )}
                      {winner.prize_distributed_at && (
                        <div className="text-sm">
                          <p className="text-gray-600">Prize Distributed At</p>
                          <p className="font-medium text-blue-600">{formatDate(winner.prize_distributed_at)}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewDetails(winner)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </Button>
                    
                    <div className="flex items-center space-x-2">
                      {winner.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleStatusUpdate(winner.id, 'verified')}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Verify
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStatusUpdate(winner.id, 'disqualified')}
                            className="text-red-600 hover:text-red-700"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Disqualify
                          </Button>
                        </>
                      )}
                      
                      {winner.status === 'verified' && (
                        <Button
                          size="sm"
                          onClick={() => handleStatusUpdate(winner.id, 'prize_distributed')}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Trophy className="w-4 h-4 mr-1" />
                          Mark Distributed
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Winner Details Dialog */}
      {selectedWinner && (
        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <Trophy className="h-5 w-5 text-yellow-600" />
                <span>Winner Details</span>
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Winner ID</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedWinner.id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Position</label>
                  <div className="mt-1">{getPositionBadge(selectedWinner.position)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">User ID</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedWinner.user_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Contest ID</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedWinner.contest_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Prize Amount</label>
                  <p className="text-sm text-green-600 font-medium mt-1">
                    {formatCurrency(selectedWinner.prize_amount)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedWinner.status)}</div>
                </div>
              </div>
              
              {/* Score */}
              {selectedWinner.score && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Score</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedWinner.score}</p>
                </div>
              )}
              
              {/* Verification Documents */}
              {selectedWinner.verification_documents && selectedWinner.verification_documents.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Verification Documents</label>
                  <div className="mt-2 space-y-2">
                    {selectedWinner.verification_documents.map((doc, index) => (
                      <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{doc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Timeline */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-3 block">Timeline</label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full"></div>
                    <div className="text-sm">
                      <span className="font-medium">Created:</span> {formatDate(selectedWinner.created_at)}
                    </div>
                  </div>
                  
                  {selectedWinner.verified_at && (
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-green-600 rounded-full"></div>
                      <div className="text-sm">
                        <span className="font-medium">Verified:</span> {formatDate(selectedWinner.verified_at)}
                      </div>
                    </div>
                  )}
                  
                  {selectedWinner.prize_distributed_at && (
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full"></div>
                      <div className="text-sm">
                        <span className="font-medium">Prize Distributed:</span> {formatDate(selectedWinner.prize_distributed_at)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default WinnerManager;